import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useI18n } from "../lib/i18n";

/**
 * SEO Phase 1 — R2 + R3
 *
 * Reusable per-page SEO head. Emits:
 *   • <title>
 *   • <meta name="description">
 *   • <link rel="canonical"> self-referencing on the current pathname
 *     (query strings are deliberately stripped so filtered views do
 *     not become canonical targets)
 *   • <meta property="og:title/description/url"> mirrored for socials
 *
 * Static index.html already carries fallback Organization schema,
 * Open Graph and Twitter base tags; Helmet overrides title/description
 * and the canonical link per page without touching Schema/hreflang
 * (both are out of scope for Phase 1).
 */
const ORIGIN = "https://cartal.sa";

export default function Seo({ title, description, canonicalPath, noindex = false, jsonLd = null, ogImage = null }) {
  const { lang } = useI18n();
  const location = useLocation();

  const fallbackTitle = lang === "ar"
    ? "كارتال — أي سيارة. أي خدمة. تحت سقف واحد."
    : "CARTAL — Every Vehicle. Every Service. One Platform.";
  const fallbackDesc = lang === "ar"
    ? "منصة كارتال — أكبر سوق متكامل لبيع وشراء السيارات وكل الخدمات المرتبطة بها في الشرق الأوسط."
    : "CARTAL — the Middle East's integrated marketplace for buying, selling, and servicing every kind of vehicle.";

  // Canonical = origin + path (NO query, NO hash). If a caller
  // provides canonicalPath explicitly we use it verbatim.
  const path = canonicalPath !== undefined ? canonicalPath : location.pathname;
  const canonical = `${ORIGIN}${path}`;

  const finalTitle = title || fallbackTitle;
  const finalDesc = description || fallbackDesc;

  // iter332 — allow callers to attach one or many JSON-LD blobs. Each
  // becomes a separate <script type="application/ld+json">. We never
  // fabricate fields; callers pass only what they can verify from
  // public page data.
  const blobs = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={canonical} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {blobs.map((obj, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
